import React from 'react';
import { Button, Text } from '@msc-b2c/shared-components';
import { CartReservationRecapStepProps, SummaryCardProps } from '@msc-b2c/interfaces/interfaces';
import styles from './cartReservationRecapStep.module.scss';
import HeadsUpInfo, { HeadsUpMessagePriority } from '../../../HeadsUpInfo';
import { HeadsUpIconAlignment } from '@msc-b2c/interfaces/enums';

import clsx from 'clsx';

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  actionLabel,
  actionIcon,
  onAction,
  isAlreadyPurchasedPassengersCard,
}) => {
  return (
    <div
      className={clsx(styles['cart-recap__card'], {
        [styles['cart-recap__card--already-purchased-passengers']]:
          isAlreadyPurchasedPassengersCard,
        [styles['cart-recap__card--interactive']]: !isAlreadyPurchasedPassengersCard,
      })}
    >
      <div
        className={clsx(styles['cart-recap__card-content'], {
          [styles['cart-recap__card-content--already-purchased-passengers']]:
            isAlreadyPurchasedPassengersCard,
        })}
      >
        <Text
          text={`${title}:`}
          styleOption="body-bold"
        />
        <Text
          text={value}
          styleOption="body-default"
        />
      </div>

      {!isAlreadyPurchasedPassengersCard && (
        <Button
          text={actionLabel}
          styleOption={clsx('cmp-button__link', styles['cart-recap__card-action'])}
          onClick={onAction}
          icon={actionIcon}
        />
      )}
    </div>
  );
};

export const CartReservationRecapStep: React.FC<CartReservationRecapStepProps> = ({
  passengersCount,
  reservations,
  isAlreadyPurchasedFlow,
  showAddReservationAction,
  showPassengerModificationBanner,
  passengerModificationBannerTitle,
  passengerModificationBannerText,
  passengerModificationBannerIcon,
  passengerModificationBannerBgColor,
  passengerModificationBannerFontColor,
  passengersLabel,
  reservationLabel,
  addReservationLabel,
  editLabel,
  addLabel,
  editIcon,
  addIcon,
  onEditPassengers,
  onAddReservation,
  onEditReservation,
}) => {
  const passengersTitle = passengersLabel ?? '';
  const reservationTitle = reservationLabel ?? '';
  const hasReservations = reservations.length > 0;

  return (
    <div className={styles['cart-recap']}>
      {showPassengerModificationBanner && passengerModificationBannerTitle && (
        <HeadsUpInfo
          icon={passengerModificationBannerIcon ?? ''}
          title={passengerModificationBannerTitle}
          description={passengerModificationBannerText ?? ''}
          priority={HeadsUpMessagePriority.DEFAULT}
          visibilityCondition={() => true}
          bgColor={passengerModificationBannerBgColor}
          fontColor={passengerModificationBannerFontColor}
          iconAlignment={HeadsUpIconAlignment.TOP}
        />
      )}

      <SummaryCard
        title={passengersTitle}
        value={String(passengersCount)}
        actionLabel={editLabel ?? ''}
        actionIcon={editIcon}
        onAction={onEditPassengers}
        isAlreadyPurchasedPassengersCard={isAlreadyPurchasedFlow}
      />

      <SummaryCard
        title={reservationTitle}
        value={hasReservations ? reservations[0].value : addReservationLabel ?? ''}
        actionLabel={hasReservations ? editLabel ?? '' : addLabel ?? ''}
        actionIcon={hasReservations ? editIcon : addIcon}
        onAction={hasReservations ? () => onEditReservation(0) : onAddReservation}
      />

      {hasReservations &&
        reservations.slice(1).map((reservation, reservationIndex) => (
          <SummaryCard
            key={`${reservation.value}-${reservationIndex + 1}`}
            title={reservationTitle}
            value={reservation.value}
            actionLabel={editLabel ?? ''}
            actionIcon={editIcon}
            onAction={() => {
              onEditReservation(reservationIndex + 1);
            }}
          />
        ))}

      {hasReservations && showAddReservationAction && (
        <SummaryCard
          title={reservationTitle}
          value={addReservationLabel ?? ''}
          actionLabel={addLabel ?? ''}
          actionIcon={addIcon}
          onAction={onAddReservation}
        />
      )}
    </div>
  );
};
